import { EyeType } from '@hiwaldo/sdk';
import { useRouter } from 'next/router';
import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Route } from '../../enums';
import {
  usePatientDoctorFormEffect,
  usePatientRegistrationFormEffect,
  usePatientPlanFormEffect,
  usePatientPrescriptionFormEffect,
  usePatientShippingFormEffect,
  PatientDoctorFormFields,
  PatientRegistrationFormFields,
  PatientPlanFormFields,
  PatientPrescriptionFormFields,
  PatientShippingFormFields,
} from '../../forms';
import {
  StoreState,
  useCreatePatientEffect,
  usePartnershipByIdEffect,
  useUpdateCustomerEffect,
} from '../../services';
import {
  PatientPageState,
  PatientPlanFormData,
  PatientPrescriptionFormData,
  PatientRegistrationFormData,
  PatientDoctorFormData,
  patientSlice,
} from '../../services/redux/partner/patient';
import { findProductVariantDataByHandle, storeRegion } from '../../utils';
import { useShoppingCartEffect } from '../common';

export type PatientStep =
  | 'doctor'
  | 'registration'
  | 'prescription'
  | 'plan'
  | 'shipping'
  | 'review'
  | 'payment';

export const usePatientEffect = (step: PatientStep) => {
  const dispatch = useDispatch();
  const router = useRouter();
  const region = storeRegion();
  const { partnershipState, patientState, memberState, customerState } =
    useSelector((state: StoreState) => {
      return {
        partnershipState: state.partner.partnership,
        patientState: state.partner.patient,
        memberState: state.partner.member.member,
        customerState: state.customers.customer.customer,
      };
    });

  const patientDoctorFormState = usePatientDoctorFormEffect(patientState);
  const patientRegistrationFormState =
    usePatientRegistrationFormEffect(patientState);
  const patientPrescriptionFormState =
    usePatientPrescriptionFormEffect(patientState);
  const patientPlanFormState = usePatientPlanFormEffect(patientState);
  const patientShippingFormState = usePatientShippingFormEffect(patientState);

  const redirectCallback =
    step === 'doctor'
      ? () => {
          router.push(Route.PATIENT_REGISTER);
        }
      : step === 'registration'
      ? () => {
          router.push(Route.PATIENT_PLAN);
        }
      : step === 'plan'
      ? () => {
          router.push(Route.PATIENT_PRESCRIPTION);
        }
      : step === 'prescription'
      ? () => {
          router.push(Route.PATIENT_SHIPPING);
        }
      : () => {
          router.push(Route.PATIENT_ORDER_REVIEW);
        };

  const createPatientState = useCreatePatientEffect(redirectCallback);
  const updateCustomerState = useUpdateCustomerEffect(redirectCallback);
  const shoppingCartState = useShoppingCartEffect(redirectCallback);

  const { findPartnershipById } = usePartnershipByIdEffect();

  useEffect(() => {
    if (!memberState?.id || 0) {
      router.push(Route.MEMBER_LOGIN);
    }

    if (
      memberState?.id &&
      memberState?.partnershipId &&
      !partnershipState.loading &&
      (!partnershipState?.data?.id ||
        +memberState.partnershipId !== +partnershipState.data?.id)
    ) {
      findPartnershipById(+memberState.partnershipId);
    }

    switch (step) {
      case 'doctor': {
        const doctorForm =
          patientDoctorFormState.getFieldValues() as PatientDoctorFormFields;

        const updatedDoctorData: PatientDoctorFormData = {
          doctorId: doctorForm.doctorId || 0,
        };

        const updatedDoctorString = JSON.stringify(updatedDoctorData);

        const currentDoctorString = JSON.stringify(
          patientState.formData.doctor
        );

        if (updatedDoctorString !== currentDoctorString) {
          dispatch(patientSlice.actions.updateDoctor(updatedDoctorData));
        }
        break;
      }
      case 'registration': {
        const registrationForm =
          patientRegistrationFormState.getFieldValues() as PatientRegistrationFormFields;

        const updatedRegistrationData: PatientRegistrationFormData = {
          memberId: +memberState?.id || 0,
          email: registrationForm.email!,
          password: registrationForm.password!,
          passwordConfirm: registrationForm.passwordConfirm!,
        };

        const updatedRegistrationString = JSON.stringify(
          updatedRegistrationData
        );

        const currentRegistrationString = JSON.stringify(
          patientState.formData.registration
        );

        if (updatedRegistrationString !== currentRegistrationString) {
          dispatch(
            patientSlice.actions.updateRegistration(updatedRegistrationData)
          );
        }
        break;
      }
      case 'prescription': {
        const prescriptionForm =
          patientPrescriptionFormState.getFieldValues() as PatientPrescriptionFormFields;

        const updatedPrescriptionData: PatientPrescriptionFormData = {
          leftEye: prescriptionForm.leftEye!,
          rightEye: prescriptionForm.rightEye!,
        };

        const updatedPrescriptionString = JSON.stringify(
          updatedPrescriptionData
        );

        const currentPrescriptionString = JSON.stringify(
          patientState.formData.prescription
        );

        if (updatedPrescriptionString !== currentPrescriptionString) {
          dispatch(
            patientSlice.actions.updatePrescription(updatedPrescriptionData)
          );
        }
        break;
      }
      case 'plan': {
        const planForm =
          patientPlanFormState.getFieldValues() as PatientPlanFormFields;

        const updatedPlanData: PatientPlanFormData = {
          subscriptionType: planForm.subscriptionType!,
          productType: planForm.productType!,
          frequency: planForm.frequency!,
        };

        const updatedPlanString = JSON.stringify(updatedPlanData);

        const currentPlanString = JSON.stringify(patientState.formData.plan);

        if (updatedPlanString !== currentPlanString) {
          dispatch(patientSlice.actions.updatePlan(updatedPlanData));
        }
        break;
      }
      case 'shipping': {
        const addressForm =
          patientShippingFormState.getFieldValues() as PatientShippingFormFields;

        const updatedAddressData = {
          matchAddress: addressForm.matchAddress || false,
          billingAddress: {
            customerId: memberState!.id || undefined,
            firstName: addressForm.billingFirstName!,
            lastName: addressForm.billingLastName!,
            company: addressForm.billingCompany!,
            line1: addressForm.billingLine1!,
            line2: addressForm.billingLine2 || undefined,
            city: addressForm.billingCity!,
            state: addressForm.billingState!,
            postalCode: addressForm.billingPostalCode!,
            country: addressForm.billingCountry!,
            phone: addressForm.billingPhone!,
          },
          shippingAddress: {
            customerId: memberState!.id || undefined,
            firstName: addressForm.shippingFirstName!,
            lastName: addressForm.shippingLastName!,
            company: addressForm.shippingCompany!,
            line1: addressForm.shippingLine1!,
            line2: addressForm.shippingLine2 || undefined,
            city: addressForm.shippingCity!,
            state: addressForm.shippingState!,
            postalCode: addressForm.shippingPostalCode!,
            country: addressForm.shippingCountry!,
            phone: addressForm.shippingPhone!,
          },
        };

        const updatedAddressString = JSON.stringify(updatedAddressData);

        const currentAddressString = JSON.stringify(
          patientState.formData.address
        );

        if (updatedAddressString !== currentAddressString) {
          dispatch(patientSlice.actions.updateAddress(updatedAddressData));
        }
        break;
      }
    }

    const updatedPageData: PatientPageState = {
      ...patientState.pageState,
      validDoctor: patientDoctorFormState.isValidDoctor,
      validRegistration: patientRegistrationFormState.isValidRegistration,
      validShipping: patientShippingFormState.isValidShipping,
      validBilling: patientShippingFormState.isValidBilling,
      validForm:
        patientDoctorFormState.isValidDoctor &&
        patientRegistrationFormState.isValidRegistration &&
        patientShippingFormState.isValidShipping &&
        patientShippingFormState.isValidBilling,
    };

    const updatedPageString = JSON.stringify(updatedPageData);

    const currentPageString = JSON.stringify(patientState.pageState);

    if (updatedPageString !== currentPageString) {
      dispatch(patientSlice.actions.updatePage(updatedPageData));
    }
  }, [
    step,
    region,
    patientDoctorFormState,
    patientRegistrationFormState,
    patientShippingFormState,
    partnershipState,
    patientState,
    memberState,
    router,
    findPartnershipById,
  ]);

  const setOpenDoctor = () => {
    router.push(Route.PATIENT_DOCTOR);
  };

  const setOpenRegistration = () => {
    router.push(Route.PATIENT_REGISTER);
  };

  const setOpenPrescription = () => {
    router.push(Route.PATIENT_PRESCRIPTION);
  };

  const setOpenPlan = () => {
    router.push(Route.PATIENT_PLAN);
  };

  const setOpenShipping = () => {
    router.push(Route.PATIENT_SHIPPING);
  };

  const setOpenSummary = () => {
    router.push(Route.PATIENT_ORDER_REVIEW);
  };

  const setOpenPayment = () => {
    router.push(Route.PATIENT_PAYMENT_DETAILS);
  };

  const pageActions = {
    setOpenDoctor,
    setOpenRegistration,
    setOpenPrescription,
    setOpenPlan,
    setOpenShipping,
    setOpenSummary,
    setOpenPayment,
  };

  const onSubmitDoctor = () => {
    const doctorForm =
      patientDoctorFormState.getFieldValues() as PatientDoctorFormFields;

    const clinic = partnershipState.data.clinics[0].clinic;

    const member = (partnershipState.data.members as any[]).find(
      (member) => +member.id === +doctorForm.doctorId!
    );

    if (clinic && member) {
      shoppingCartState.addDoctorToCart({
        doctorId: undefined,
        firstName: member.firstName,
        lastName: member.lastName,
        company: partnershipState.data.company as string,
        address1: clinic.address1 as string,
        address2: clinic.address2 as string,
        city: clinic.city as string,
        zip: clinic.zip as string,
        state: clinic.state as string,
        country: clinic.country as string,
        phone: clinic.phone as string,
      });
    }
  };

  const onSubmitPatient = () => {
    const registrationForm =
      patientRegistrationFormState.getFieldValues() as PatientRegistrationFormFields;
    const addressForm =
      patientShippingFormState.getFieldValues() as PatientShippingFormFields;

    const shippingAddressInput =
      addressForm.shippingFirstName &&
      addressForm.shippingLine1 &&
      addressForm.shippingCity &&
      addressForm.shippingState &&
      addressForm.shippingPostalCode
        ? {
            firstName: addressForm.shippingFirstName!,
            lastName: addressForm.shippingLastName!,
            company: addressForm.shippingCompany || undefined,
            line1: addressForm.shippingLine1!,
            line2: addressForm.shippingLine2 || undefined,
            city: addressForm.shippingCity!,
            state: addressForm.shippingState!,
            postalCode: addressForm.shippingPostalCode!,
            country: addressForm.shippingCountry!,
            phone: addressForm.shippingPhone!,
          }
        : undefined;

    const billingAddressInput =
      addressForm.billingFirstName &&
      addressForm.billingLine1 &&
      addressForm.billingCity &&
      addressForm.billingState &&
      addressForm.billingPostalCode
        ? {
            firstName: addressForm.billingFirstName!,
            lastName: addressForm.billingLastName!,
            company: addressForm.billingCompany || undefined,
            line1: addressForm.billingLine1!,
            line2: addressForm.billingLine2 || undefined,
            city: addressForm.billingCity!,
            state: addressForm.billingState!,
            postalCode: addressForm.billingPostalCode!,
            country: addressForm.billingCountry!,
            phone: addressForm.billingPhone!,
          }
        : undefined;

    const customerInput = {
      region,
      memberId: +memberState?.id || 0,
      email: registrationForm.email!,
      password: registrationForm.password!,
      firstName: addressForm.shippingFirstName!,
      lastName: addressForm.shippingLastName!,
      matchAddress: addressForm.matchAddress || false,
      billingAddress: billingAddressInput,
      shippingAddress: shippingAddressInput,
    };

    if (customerState?.id) {
      updateCustomerState.updateCustomer(customerInput);
    } else {
      createPatientState.createPatient(customerInput);
    }
  };

  const onSubmitPlan = () => {
    const planForm =
      patientPlanFormState.getFieldValues() as PatientPlanFormFields;

    let trialId = 0;

    switch (planForm.subscriptionType) {
      case 'Monthly':
        trialId = planForm.productType === 'Hydra Boost' ? 1 : 3;
        break;
      case 'Quarterly':
        trialId = planForm.productType === 'Hydra Boost' ? 1 : 3;
        break;
      case 'Bi-Annual':
        trialId = planForm.productType === 'Hydra Boost' ? 1 : 3;
        break;
      case 'Annual':
        trialId = planForm.productType === 'Hydra Boost' ? 1 : 3;
        break;
    }

    shoppingCartState.addTrialToCart(trialId);
  };

  const onSubmitPrescription = () => {
    const planForm =
      patientPlanFormState.getFieldValues() as PatientPlanFormFields;
    const prescriptionForm =
      patientPrescriptionFormState.getFieldValues() as PatientPrescriptionFormFields;

    console.log('SUBMIT PLAN', { prescriptionForm });

    let leftEye = undefined;
    let leftProductVariant: {
      id: number;
      handle: string;
      power?: string | undefined;
      productId: number;
    };

    if (prescriptionForm.leftEye !== 'N/A') {
      const handlePrefix =
        planForm.productType === 'Hydra Boost'
          ? 'daily-lenses-'
          : 'vitamin-lenses-';
      leftProductVariant = findProductVariantDataByHandle(
        handlePrefix + prescriptionForm.leftEye
      )!;

      leftEye = {
        eye: EyeType.LEFT,
        productVariantId: leftProductVariant ? leftProductVariant.id : 0,
        power: leftProductVariant
          ? (leftProductVariant.power as string)
          : 'N/A',
      };
    }

    let rightEye = undefined;
    let rightProductVariant: {
      id: number;
      handle: string;
      power?: string | undefined;
      productId: number;
    };

    if (prescriptionForm.rightEye !== 'N/A') {
      const handlePrefix =
        planForm.productType === 'Hydra Boost'
          ? 'daily-lenses-'
          : 'vitamin-lenses-';
      rightProductVariant = findProductVariantDataByHandle(
        handlePrefix + prescriptionForm.rightEye
      )!;

      rightEye = {
        eye: EyeType.RIGHT,
        productVariantId: rightProductVariant ? rightProductVariant.id : 0,
        power: rightProductVariant
          ? (rightProductVariant.power as string)
          : 'N/A',
      };
    }

    shoppingCartState.addPrescriptionToCart(leftEye, rightEye);
  };

  return {
    shoppingCartState,
    patientDoctorFormState,
    patientRegistrationFormState,
    patientPrescriptionFormState,
    patientPlanFormState,
    patientShippingFormState,
    partnershipState: partnershipState.data,
    patientState,
    memberState,
    pageActions,
    onSubmitDoctor,
    onSubmitPatient,
    onSubmitPlan,
    onSubmitPrescription,
    loading: updateCustomerState.loading || createPatientState.loading,
    success: updateCustomerState.success || createPatientState.success,
    error: createPatientState.error
      ? createPatientState.error
      : updateCustomerState.error
      ? updateCustomerState.error
      : undefined,
  };
};
